import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { commonErrorResponses, emptyEnvelopeSchema, requiredPayloadEnvelope, } from '../envelope.js';
/** set은 폴백 루트, group은 범위 버킷, exact는 한 수량이다. 한 테이블에 세 갈래를 두어 종류별 테이블을 만들지 않는다. */
export const signatureKindSchema = z.enum(['set', 'group', 'exact']);
/** v1 시그니처 유형이다. donation은 후원 개수, subscription은 구독 개월이며 balloon/star는 쓰지 않는다. */
export const signatureEventTypeSchema = z.enum(['donation', 'subscription']);
/** auto는 미디어 길이를 따르고, manual은 스트리머가 초를 직접 넣어 재생 끝을 고정한다. */
export const signatureDurationModeSchema = z.enum(['auto', 'manual']);
/** 수량의 단위 이름이다. 개수 후원의 count는 보통 생략하고, 통화가 갈릴 때만 채운다. */
export const signatureUnitSchema = z.string().min(1).max(32);
/** 시그니처 한 행의 HTTP 표현이다. 트리거는 eventType + quantity(+ 필요 시 unit)이고 donationType 컬럼을 되돌리지 않는다. */
export const signatureSchema = z.object({
    /** 시그니처 행의 기본 키다. UUID로 생성해 경로 파라미터와 맞춘다. */
    id: z.string(),
    /** set(폴백 루트) / group(범위) / exact(정확 수량)다. 종류별 리소스를 나누지 않는다. */
    kind: signatureKindSchema,
    /** 맞출 경제 이벤트 유형이다. donation은 후원, subscription은 구독이며 서로 매칭하지 않는다. */
    eventType: signatureEventTypeSchema,
    /** 같은 eventType을 단위로 나눌 때만 채운다. null이면 그 유형의 기본 단위(개수는 count 생략)다. */
    unit: signatureUnitSchema.nullable(),
    /** 목록·편집기에 보이는 표시 이름이다. 매칭 키가 아니므로 바꿔도 트리거는 그대로다. */
    name: z.string(),
    /** 꺼 두면 슬롯에 붙어 있어도 트리거하지 않는다. 방송 중 실시간 토글용이다. */
    active: z.boolean(),
    /** exact가 속한 범위 그룹이다. set·group은 null로 두어 깊이를 1로 고정한다. */
    parentId: z.string().nullable(),
    /** exact의 정확 수량이다. donation이면 개수, subscription이면 개월이다. set·group은 null이다. */
    exactValue: z.number().int().nullable(),
    /** group 범위의 하한(이상)이다. exact·set은 null이다. */
    minValue: z.number().int().nullable(),
    /** group 범위의 상한(이하, inclusive)이다. null이면 상한 없이 맞춘다. */
    maxValue: z.number().int().nullable(),
    /** 연출 이미지의 보관함 파일이다. null이면 세트/그룹 상속 또는 빈 슬롯이다. */
    imageFileId: z.string().nullable(),
    /** imageFileId의 표시 이름이다. 목록을 조인 없이 그리려고 복제한다. */
    imageFileName: z.string().nullable(),
    /** 연출 사운드의 보관함 파일이다. null이면 상속 또는 무음이다. */
    soundFileId: z.string().nullable(),
    /** soundFileId의 표시 이름이다. 사운드 슬롯을 조인 없이 그린다. */
    soundFileName: z.string().nullable(),
    /** 사운드 길이(초)다. durationMode=auto일 때 알림이 끝나는 시각의 입력이다. */
    soundDurationSec: z.number().int().nullable(),
    /** 알림 문구 템플릿이다. 토큰은 재생 시 치환하며, 비어 있어도 행은 성립한다. */
    textTemplate: z.string(),
    /** auto면 미디어 길이로 끝내고, manual이면 manualDurationSec를 쓴다. */
    durationMode: signatureDurationModeSchema,
    /** durationMode=manual일 때만 쓰는 표시 지속 시간(초)이다. auto면 null이다. */
    manualDurationSec: z.number().int().nullable(),
    /** 행이 만들어진 시각의 ISO 문자열이다. */
    createdAt: z.string(),
    /** 마지막 수정 시각의 ISO 문자열이다. */
    updatedAt: z.string(),
});
/** 생성·수정의 연출·수량 패치다. undefined는 유지, null은 비움이며 kind·eventType은 여기서 바꾸지 않는다. */
const playbackFieldsSchema = z.object({
    /** 표시 이름이다. 매칭 키가 아니므로 비우면 기존 값을 유지한다. */
    name: z.string().min(1).max(200).optional(),
    /** 트리거 on/off다. 생략하면 기존 활성 상태를 유지한다. */
    active: z.boolean().optional(),
    /** exact의 부모 그룹이다. null은 그룹에서 떼고, undefined는 기존 parentId를 유지한다. */
    parentId: z.string().nullable().optional(),
    /** 트리거 단위다. null은 기본 단위로 되돌리고, undefined는 기존 unit을 유지한다. */
    unit: signatureUnitSchema.nullable().optional(),
    /** exact 수량이다. 양수만 받으며, set·group 패치에서는 쓰지 않는다. */
    exactValue: z.number().int().positive().nullable().optional(),
    /** group 하한(이상)이다. 양수만 받으며, 범위를 접을 때는 서비스가 거절한다. */
    minValue: z.number().int().positive().nullable().optional(),
    /** group 상한(이하, inclusive)이다. null은 상한 없음을 뜻한다. */
    maxValue: z.number().int().positive().nullable().optional(),
    /** 연출 이미지 파일이다. null은 이미지를 떼고, 본인 보관함 밖 id는 거절한다. */
    imageFileId: z.string().nullable().optional(),
    /** imageFileId의 표시 이름이다. 파일 id와 같이 보내 목록 조인을 피한다. */
    imageFileName: z.string().nullable().optional(),
    /** 연출 사운드 파일이다. null은 사운드를 떼고, 본인 보관함 밖 id는 거절한다. */
    soundFileId: z.string().nullable().optional(),
    /** soundFileId의 표시 이름이다. 사운드 슬롯을 조인 없이 그린다. */
    soundFileName: z.string().nullable().optional(),
    /** 사운드 길이(초)다. auto 지속 시간의 입력이며 양수만 받는다. */
    soundDurationSec: z.number().int().positive().nullable().optional(),
    /** 알림 문구 템플릿이다. 생략하면 기존 문구를 유지한다. */
    textTemplate: z.string().max(1000).optional(),
    /** auto/manual 전환이다. 생략하면 기존 모드를 유지한다. */
    durationMode: signatureDurationModeSchema.optional(),
    /** manual 모드의 표시 지속 시간(초)이다. auto로 바꿀 때는 비운다. */
    manualDurationSec: z.number().int().positive().nullable().optional(),
});
/** 새 시그니처 행이다. kind와 eventType은 삽입 시에만 정하고 PATCH로 가족을 바꾸지 않는다. */
export const signatureCreateBodySchema = playbackFieldsSchema.extend({
    /** set / group / exact다. 삽입 뒤 종류를 바꾸면 트리 불변식이 깨지므로 생성 전용이다. */
    kind: signatureKindSchema,
    /** 맞출 이벤트 유형이다. 삽입 뒤 바꾸면 형제 겹침 검사가 어긋나 생성 전용이다. */
    eventType: signatureEventTypeSchema,
    /** 목록에 보일 표시 이름이다. 빈 문자열은 거절한다. */
    name: z.string().min(1).max(200),
});
/** 기존 행의 연출·수량 패치다. kind와 eventType은 빼 두어 트리거 가족이 건너뛰지 않게 한다. */
export const signatureUpdateBodySchema = playbackFieldsSchema;
const c = initContract();
/** 세션 사용자의 시그니처 CRUD다. 목록은 본인 행만 주고, 겹침·부모·미디어 소유는 user-api가 검사한다. */
export const userSignaturesContract = c.router({
    list: {
        method: 'GET',
        path: '/signatures',
        query: z.object({
            /** 후원/구독 화면을 나누려고 한 유형만 고른다. 없으면 둘 다 준다. */
            eventType: signatureEventTypeSchema.optional(),
            /** 같은 유형을 단위로 나눈 세트를 고른다. 없으면 그 유형의 모든 단위를 준다. */
            unit: signatureUnitSchema.optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                items: z.array(signatureSchema),
            })),
            ...commonErrorResponses,
        },
    },
    get: {
        method: 'GET',
        path: '/signatures/:signatureId',
        pathParams: z.object({ signatureId: z.string() }),
        responses: {
            200: requiredPayloadEnvelope(z.object({ signature: signatureSchema })),
            ...commonErrorResponses,
        },
    },
    create: {
        method: 'POST',
        path: '/signatures',
        body: signatureCreateBodySchema,
        responses: {
            201: requiredPayloadEnvelope(z.object({ signature: signatureSchema })),
            ...commonErrorResponses,
        },
    },
    update: {
        method: 'PATCH',
        path: '/signatures/:signatureId',
        pathParams: z.object({ signatureId: z.string() }),
        body: signatureUpdateBodySchema,
        responses: {
            200: requiredPayloadEnvelope(z.object({ signature: signatureSchema })),
            ...commonErrorResponses,
        },
    },
    remove: {
        method: 'DELETE',
        path: '/signatures/:signatureId',
        pathParams: z.object({ signatureId: z.string() }),
        body: z.null().optional(),
        responses: {
            200: emptyEnvelopeSchema,
            ...commonErrorResponses,
        },
    },
});
//# sourceMappingURL=signatures.contract.js.map